import React from 'react';
import {classes, style} from './doppe-action-type-testimonial-list-view-item.st.css';
import {flexColumn, flexRowAlignToCenter, ImageViewResizeMode, reactCreateMemo} from '@wix/devzai-utils-react';
import {evaluateFunction} from '@wix/devzai-utils-common';
import {
    DoppeActionLayout,
    doppeHideableValueIsVisibleAndNotEqualValue,
    DoppeListActionSettings
} from '../../../../client-server-common';
import {WidgetRenderingContextType} from '../../../../client-server-common/doppe-action-types/doppe-action-type';
import {IDoppeViewerActionContext} from '../../../../doppe-sdk';
import {
    useAssertWidgetRenderingContextType
} from '../../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';
import {
    DoppeActionTypeTestimonialsList
} from '../../../doppe-action-type-testimonials-list/doppe-action-type-testimonials-list';
import {
    DoppeActionTypeBaseListViewItem
} from '../doppe-action-type-base-list-view-item/doppe-action-type-base-list-view-item';
import {
    DoppeActionTypeCommonListViewItem
} from '../doppe-action-type-common-list-view-item/doppe-action-type-common-list-view-item';
import {
    DoppeActionTypeRatingView
} from '../../../../components/doppe-action-type-rating-view/doppe-action-type-rating-view';
import {
    bymoPageStyleGetStyleScopeClassName,
    BymoPageStyleScope,
    BymoPageStyleScopeContext
} from '../../../../components/bymo-page-style/bymo-page-style';
import {DoppeActionTypeTestimonial} from '../../../doppe-action-type-testimonial/doppe-action-type-testimonial';
import {CommonClasses, FlexLayout, WixImageView} from '@wix/devzai-common-client';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeActionTypeWidgetShowMoreButton
} from '../../../../components/doppe-action-type-widget-show-more-button/doppe-action-type-widget-show-more-button';
import {
    DoppeActionTypeDetailsListViewItem, DoppeActionTypeDetailsListViewItemContentAlignment
} from '../doppe-action-type-details-list-view-item/doppe-action-type-details-list-view-item';

const WidgetRenderingContextTypeCssState = {
    InPage: 'inPage',
    Modal: 'modal',
    Strip: 'strip',
}

export const DoppeActionTypeTestimonialListViewItem = reactCreateMemo(function DoppeActionTypeListViewItem<PROPS extends DoppeListActionSettings.WithItemClickBehaviour>(
    props: {
        listItemSettings: DoppeActionTypeTestimonialsList.TestimonialItemSettings;
        layout: DoppeActionLayout;
        isOneItem: boolean;
        itemShowRating: boolean;
        hideImageWhenPossible?: boolean;
        hideTitleWhenPossible?: boolean;
        hideDescriptionWhenPossible?: boolean;
        actionContext?: IDoppeViewerActionContext<PROPS>;
        innerItemIndex: number;
        innerItemId: string;
    }
) {
    const {
        listItemSettings,
        layout,
        isOneItem = false,
        itemShowRating = false,
        hideImageWhenPossible = false,
        hideTitleWhenPossible = false,
        hideDescriptionWhenPossible = false,
        actionContext,
        innerItemIndex,
        innerItemId,
    } = props;

    const {
        ribbon,
        description,
        rating,
        name,
        tagLine,
        mainQuote,
        profileMedia,
        date,
    } = listItemSettings;


    const widgetRenderingContextType = useAssertWidgetRenderingContextType();

    const styleContext = evaluateFunction(() => {
        switch (widgetRenderingContextType) {
            case WidgetRenderingContextType.Modal: {
                return BymoPageStyleScope.Popup;
            }
            case WidgetRenderingContextType.Card: {
                return BymoPageStyleScope.Card;
            }
            case WidgetRenderingContextType.Strip: {
                return BymoPageStyleScope.Card;
            }
        }
    });


    const testimonalListItem = (
        <BymoPageStyleScopeContext.Provider
            value={styleContext}
        >
            <DoppeActionTypeBaseListViewItem
                className={style(classes.root, {
                        layout: layout,
                        isOneItem: isOneItem,
                        widgetRenderingContextType: evaluateFunction(() => {
                            switch (widgetRenderingContextType) {
                                case WidgetRenderingContextType.Modal: {
                                    return WidgetRenderingContextTypeCssState.Modal;
                                }
                                case WidgetRenderingContextType.Card: {
                                    return WidgetRenderingContextTypeCssState.InPage;
                                }
                                case WidgetRenderingContextType.Strip: {
                                    return WidgetRenderingContextTypeCssState.Strip;
                                }
                            }
                        }),
                    }, flexColumn(),
                    bymoPageStyleGetStyleScopeClassName(styleContext)
                )}

                contentClassName={style(classes.content, {}, flexColumn())}
                actionContext={actionContext}
                innerItemIndex={innerItemIndex}
                innerItemId={innerItemId}
            >
                <DoppeActionTypeTestimonialWidgetListItem
                    actionSettings={{
                        profileMedia: profileMedia,
                        ribbon: ribbon,
                        description: description,
                        name: name,
                        mainQuote: mainQuote,
                        tagLine: tagLine,
                        date: date,
                        rating: rating,
                        ctaButtons: [],
                        sections: [],
                    }}
                />
            </DoppeActionTypeBaseListViewItem>
        </BymoPageStyleScopeContext.Provider>
    );

    switch (layout) {
        case DoppeActionLayout.Column:
        case DoppeActionLayout.ImagesGrid:
         {
            return <DoppeActionTypeCommonListViewItem
                actionContext={actionContext}
                innerItemId={innerItemId}
                innerItemIndex={innerItemIndex}
                title={name}
                description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                image={profileMedia}
                layout={layout}
                ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? ribbon : undefined}
                price={itemShowRating && layout === DoppeActionLayout.Column ? <DoppeActionTypeRatingView rating={rating}/> : undefined}
                isOneItem={isOneItem}
                hideImageWhenPossible={hideImageWhenPossible}
                hideTitleWhenPossible={hideTitleWhenPossible}
                hideDescriptionWhenPossible={hideDescriptionWhenPossible}
            />;
        }
        case DoppeActionLayout.Grid:
        case DoppeActionLayout.CarouselSmall: {
            return <DoppeActionTypeDetailsListViewItem
                actionContext={actionContext}
                innerItemId={innerItemId}
                innerItemIndex={innerItemIndex}
                title={name}
                contentChildren={itemShowRating ? <DoppeActionTypeRatingView rating={rating}/> : undefined}
                image={profileMedia}
                layout={layout}
                ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? ribbon : undefined}
                isOneItem={isOneItem}
                hideTitleWhenPossible={hideTitleWhenPossible}
                hideDescriptionWhenPossible={hideDescriptionWhenPossible}
                alignment={layout ===  DoppeActionLayout.CarouselSmall ? DoppeActionTypeDetailsListViewItemContentAlignment.Start : DoppeActionTypeDetailsListViewItemContentAlignment.Center}
            />;
        }
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Swiper:
        case DoppeActionLayout.Carousel: {
            return testimonalListItem;
        }

    }
});


export const DoppeActionTypeTestimonialWidgetListItem = React.memo(function DoppeActionTypeTestimonialWidgetListItem(
    props: {
        actionSettings: DoppeActionTypeTestimonial.ActionSettings;
    }
) {
    const {
        actionSettings,
    } = props;


    const {
        profileMedia,
        description,
        tagLine,
        mainQuote,
        name,
        rating
    } = actionSettings;

    return (
        <div
            className={style(classes.listViewItemRootContent, {}, flexColumn())}
        >
            <div
                className={style(classes.detailsSection, {}, flexRowAlignToCenter())}
            >
                {
                    profileMedia ?
                        <WixImageView
                            className={style(classes.profileImage, FlexLayout.noShrink)}
                            src={profileMedia}
                            resizeMode={ImageViewResizeMode.Cover}
                            positionHorizontal={'center'}
                            positionVertical={'center'}
                        />
                        :
                        null
                }
                <div>
                    <DoppeActionTypeWidgetInnerTitle>
                        {name}
                    </DoppeActionTypeWidgetInnerTitle>
                    <DoppeActionTypeWidgetInnerDescription>
                        {tagLine}
                    </DoppeActionTypeWidgetInnerDescription>
                </div>
            </div>
            <DoppeActionTypeWidgetInnerTitle
                className={style(classes.mainQuote, {}, CommonClasses.lineClamp(2))}
            >
                {mainQuote}
            </DoppeActionTypeWidgetInnerTitle>
            <DoppeActionTypeWidgetInnerDescription
                className={style(classes.description, {})}
                forceMaxLines={true}
                maxLines={3}
                showSeeMoreButton={false}
            >
                {description}
            </DoppeActionTypeWidgetInnerDescription>
            <DoppeActionTypeRatingView
                className={style(classes.rating, {})}
                rating={rating}
            />
            <DoppeActionTypeWidgetShowMoreButton
                isOpen={false}
            />
        </div>
    );
});

